'use client';

import React, { useState } from 'react';
import { PulseButton, Dropdown } from 'src/components/PulseUI';
import { faDownload } from '@fortawesome/pro-solid-svg-icons';
import { getActivityExport } from 'src/api/activities/activitiesExport';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';

interface ActivityExportProps {
  disabled?: boolean;
  exportSearchParams: any;
}

const ActivityExport: React.FC<ActivityExportProps> = ({
  disabled = false,
  exportSearchParams,
}) => {
  const { setNotification } = useNotification();
  const log = useLogging();

  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async (type: string) => {
    const date = new Date().toISOString().split('T')[0];
    const fileName = `activity-export-${date}`;

    setIsExporting(true);
    try {
      const res = await getActivityExport({
        searchParams: exportSearchParams,
        fileType: type,
        fileName,
      });

      if (!res) return;
      setNotification({
        title: 'Success',
        message:
          'Export initiated - You will receive an email when it is ready for download',
        type: 'success',
      });
      log.event('Export', {
        type: 'activities',
        source: 'Activity Export',
      });
    } catch (err: any) {
      log.exception(`Error exporting Activities: ${err}`);

      setNotification({
        title: 'Error',
        message: err?.error_message || 'Error exporting, please try again.',
        type: 'error',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const exportOptions = [
    {
      label: 'CSV',
      onClick: () => handleExport('csv'),
    },
    {
      label: 'Excel (XLSX)',
      onClick: () => handleExport('xlsx'),
    },
  ];

  return (
    <Dropdown
      options={exportOptions}
      slots={{
        trigger: {
          component: (
            <PulseButton
              data-testid="activity-export-menu-button"
              startDecorator={faDownload}
              variant="soft"
              color="neutral"
              size="md"
              loading={isExporting}
              disabled={disabled}
              aria-label="Export activity data"
              className="font-semibold"
            >
              Export
            </PulseButton>
          ),
        },
        option: {
          component: (option) => (
            <div
              id={`activity-export-menu-item-${option.label
                .toLowerCase()
                .replace(' ', '-')}`}
              data-testid={`activity-export-menu-item-${option.label
                .toLowerCase()
                .replace(' ', '-')}`}
              className="text-sm font-medium"
            >
              {option.label}
            </div>
          ),
        },
      }}
      className="z-10 shadow-md"
      position="bottom-right"
    />
  );
};

export default ActivityExport;
